/**
 * `data-screen-label="Agent"`, tab `channels` — **Pass to another agent** (plan-021 WAPP-PASS; design §33.13.2; ADR 0074,
 * proposed; OD-4, OD-19, OD-24 at their recommended defaults, accepted by the owner at AP-0, 2026-10-19).
 *
 * Drawn under "Put callers through to a person", in the same card grammar, with ADR 0057's console look. No new screen
 * and no `data-screen-label`, so `pnpm check:screens` is unchanged (OD-19). Copy is §2.12 / §33.14 verbatim
 * (`./voice-agent-pass-copy.ts`); Claude Design's text replaces it at AP-3.
 *
 * ```
 * Pass to another agent                                              [Not available yet]
 *   dek
 *   the "not available yet" note while the api switch is off — editing and saving still work (OD-24)
 *   one row per target: name · audience tag · Remove; its state and warnings; When to pass; How (two choices);
 *     May pass the caller back (a switch); What to tell the next agent (warm only)
 *   This agent keeps every conversation itself.   (no target)
 *   [Add an agent]  → a picker over the workspace's other agents; one that cannot receive is drawn disabled with why
 *   [Save as a draft] [Discard]   problems, refusals, "Saved as a draft…" / "Nothing changed."
 *   [Publish now]   (a draft exists) → the existing publish, with its `409 draft_base_changed` answer
 *   Changes apply once the draft is published; conversations already in progress continue as they started.
 * ```
 *
 * A SAVE IS A DRAFT (OD-4): `PUT …/pass-targets` with the `live_version_seq` read makes a derived, unpublished
 * agent_version; nothing changes for callers until it is published. The editor starts from the newest pass-target
 * draft when there is one, else from the published list. `changed: false` saves nothing and says so. A `409
 * live_version_seq_stale` re-reads, drops the edits and says so. A refusal about one entry is said under that entry.
 *
 * UNDOING A DRAFT (review M1): the API compares a save with the PUBLISHED list, so a member who edits a draft back to
 * the published list gets `changed: false` while the draft still waits. The panel says exactly that — the list sent is
 * the published one, the draft {label} still differs and waits on Versions — keeps the published list in the editor
 * (never snapping back to the draft's rows unannounced), and offers "Publish {label}" and "Open {label} on Versions".
 * There is no discard of a draft to offer: Versions has none.
 *
 * ACCESSIBILITY (review L3): each inline problem is the `aria-describedby` of its input; each Remove names its agent;
 * after a save, a publish or a remove, focus moves to the outcome line or the list, never to the page body.
 *
 * SHOWN ONLY WHEN `offered` (the source's agent_template may pass, the catalogue): a survey or help VoiceAgent draws
 * nothing. Without a published version the editor is not drawn — the API refuses a save then (`agent_version_not_ready`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { VOICE_AGENT_PASS_TARGET_LIMIT, type VoiceAgentAudience, type VoiceAgentPassMode } from "@wetalk/domain";
import { Button, Dialog, Icon, Tag } from "@wetalk/ui";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import "./voice-agent-pass.css";

import { apiErrorText } from "../../../../api/api-error.js";
import { useGateway } from "../../../../api/provider.js";
import { QUERY_KEY } from "../../../../api/query.js";
import { draftBaseChangedOf } from "../../../../api/slice/optimisation-review.js";
import {
  VOICE_AGENT_PASS_QUERY_KEY,
  useVoiceAgentPassTargets,
  type PassCandidateView,
  type PassTargetChange,
  type PassTargetEntryView,
  type PassTargetState,
  type PassTargetWarningView,
  type VoiceAgentPassTargetsView,
} from "../../../../api/slice/voice-agent-pass.js";
import { portalText, usePortalTranslation } from "../../../../i18n/portal.js";
import { GLYPH } from "../../../../shell/glyph.js";
import { useMemberCan } from "../../../../shell/member-capability.js";
import { SwitchControl } from "../../../billing/switch-control.js";
import { AudienceTag, voiceAgentAudienceOf } from "../../agent-audience.js";
import {
  candidateRefusalTextOf,
  noteProblemOf,
  saveRefusalTextOf,
  targetLimitText,
  targetStateTextOf,
  warningTextOf,
  whenProblemOf,
  type PassTargetRefusalText,
} from "./voice-agent-pass-copy.js";

export interface VoiceAgentPassPanelProps {
  readonly voiceAgentId: string;
  /** The live version's languages: a warning naming every one of them is "speaks none of this agent's languages". */
  readonly languageCode: readonly string[];
  /** The source's agent_template, for the audience the picker explains a crossed wall with; `null` while unread. */
  readonly agentTemplateCode: string | null;
  /** Versions with this agent_version selected — where a waiting draft is seen (review M1). */
  readonly onOpenAgentVersion: (agentVersionId: string) => void;
}

/** One row of the editor: what is sent, plus what the panel draws beside it. */
interface TargetRow {
  readonly targetVoiceAgentId: string;
  readonly displayName: string;
  readonly audience: VoiceAgentAudience | null;
  readonly state: PassTargetState;
  readonly warning: readonly PassTargetWarningView[];
  readonly when: string;
  readonly mode: VoiceAgentPassMode;
  readonly mayPassBack: boolean;
  /** "" for none; dropped on a cold entry. */
  readonly note: string;
}

function rowOf(entry: PassTargetEntryView): TargetRow {
  return {
    targetVoiceAgentId: entry.targetVoiceAgentId,
    displayName: entry.displayName,
    audience: entry.audience,
    state: entry.state,
    warning: entry.warning,
    when: entry.when,
    mode: entry.mode,
    mayPassBack: entry.mayPassBack,
    note: entry.note ?? "",
  };
}

/** A candidate just added: warm, not passing back, nothing written yet. Its warnings arrive with the save. */
function rowOfCandidate(candidate: PassCandidateView): TargetRow {
  return {
    targetVoiceAgentId: candidate.voiceAgentId,
    displayName: candidate.displayName,
    audience: candidate.audience,
    state: candidate.state,
    warning: [],
    when: "",
    mode: "warm",
    mayPassBack: false,
    note: "",
  };
}

function changeOf(row: TargetRow): PassTargetChange {
  const note = row.note.trim();
  return {
    targetVoiceAgentId: row.targetVoiceAgentId,
    when: row.when.trim(),
    mode: row.mode,
    mayPassBack: row.mayPassBack,
    note: row.mode === "cold" || note === "" ? null : note,
  };
}

function sameRows(left: readonly TargetRow[], right: readonly TargetRow[]): boolean {
  if (left.length !== right.length) return false;
  return left.every((one, index) => {
    const other = right[index];
    if (other === undefined) return false;
    const a = changeOf(one);
    const b = changeOf(other);
    return a.targetVoiceAgentId === b.targetVoiceAgentId && a.when === b.when && a.mode === b.mode && a.mayPassBack === b.mayPassBack && a.note === b.note;
  });
}

/** The list the editor starts from: the newest pass-target draft, else the published version's. */
function storedRowsOf(stored: VoiceAgentPassTargetsView): readonly TargetRow[] {
  return (stored.draft ?? stored.liveVersion)?.target.map(rowOf) ?? [];
}

/**
 * What the last save said. `equals_published`: the list sent is the published one while a draft waits (review M1) —
 * the label is the draft's, read when the save was sent.
 */
type SavedLine = "draft" | "unchanged" | "stale" | { readonly kind: "equals_published"; readonly agentVersionId: string; readonly label: string } | null;

/** Where focus goes after an act that removes the button that was pressed (review L3). */
type FocusTarget = "outcome" | "list" | { readonly refusalOf: string } | null;

type PublishOutcome =
  | { readonly kind: "published"; readonly label: string }
  | { readonly kind: "base_changed"; readonly label: string; readonly message: string; readonly agentVersionId: string }
  | { readonly kind: "refused"; readonly sentence: string };

function isStale(error: unknown): boolean {
  return (error instanceof ApiError && error.code === "live_version_seq_stale") || (error instanceof Error && error.name === "StaleLiveVersionSeqError");
}

export function VoiceAgentPassPanel({ voiceAgentId, languageCode, agentTemplateCode, onOpenAgentVersion }: VoiceAgentPassPanelProps): ReactNode {
  usePortalTranslation("voiceAgentPass");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const canEdit = useMemberCan().can("voice_agent.write");
  const read = useVoiceAgentPassTargets(voiceAgentId);
  const [edited, setEdited] = useState<readonly TargetRow[] | null>(null);
  const [savedLine, setSavedLine] = useState<SavedLine>(null);
  const [refusal, setRefusal] = useState<(PassTargetRefusalText & { readonly targetVoiceAgentId: string | null }) | null>(null);
  const [publishOutcome, setPublishOutcome] = useState<PublishOutcome | null>(null);
  const [picking, setPicking] = useState(false);
  // Set beside the state an act changes, so the render that draws its outcome also moves the focus.
  const focusTo = useRef<FocusTarget>(null);
  const setFocusTo = (target: FocusTarget): void => {
    focusTo.current = target;
  };
  const outcomeRef = useRef<HTMLSpanElement>(null);
  const listRef = useRef<HTMLElement>(null);
  const idBase = useId();

  // Review L3: once the outcome or the list is drawn, it takes the focus the pressed button had. Runs after every
  // render; does nothing unless an act asked for it.
  useEffect(() => {
    const target = focusTo.current;
    if (target === null) return;
    focusTo.current = null;
    if (target === "outcome") outcomeRef.current?.focus();
    else if (target === "list") listRef.current?.focus();
    else globalThis.document.getElementById(`${idBase}-refusal-${target.refusalOf}`)?.focus();
  });

  // Every pass read: this list, and the referrers another agent's confirmation reads.
  const reread = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: VOICE_AGENT_PASS_QUERY_KEY.all });
  };

  const save = useMutation({
    mutationFn: (input: { readonly rows: readonly TargetRow[]; readonly stored: VoiceAgentPassTargetsView }) =>
      gateway.changeVoiceAgentPassTargets(voiceAgentId, input.rows.map(changeOf), input.stored.liveVersionSeq),
    onSuccess: async (saved, input) => {
      setEdited(null);
      setRefusal(null);
      setPublishOutcome(null);
      if (saved.changed && saved.draftAgentVersionId !== null && saved.draftLabel !== null) {
        queryClient.setQueryData<VoiceAgentPassTargetsView>(VOICE_AGENT_PASS_QUERY_KEY.targets(voiceAgentId), {
          ...input.stored,
          draft: { agentVersionId: saved.draftAgentVersionId, label: saved.draftLabel, baseAgentVersionId: saved.baseAgentVersionId, target: saved.target },
        });
        setSavedLine("draft");
        // The new draft is on Versions too.
        await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      } else if (input.stored.draft !== null) {
        // Review M1: the list sent equals the PUBLISHED one (the API's comparison) while a draft waits. Keep what was
        // sent in the editor — the published list — and say so; the draft is untouched.
        setEdited(input.rows);
        setSavedLine({ kind: "equals_published", agentVersionId: input.stored.draft.agentVersionId, label: input.stored.draft.label });
      } else {
        setSavedLine("unchanged");
      }
      setFocusTo("outcome");
      await reread();
    },
    onError: async (error, input) => {
      if (isStale(error)) {
        setEdited(null);
        setRefusal(null);
        setSavedLine("stale");
        setFocusTo("outcome");
        await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
        await reread();
        return;
      }
      const text = saveRefusalTextOf(error);
      const about = text.index === null ? undefined : input.rows[text.index];
      setRefusal({ ...text, targetVoiceAgentId: about === undefined ? null : about.targetVoiceAgentId });
      setFocusTo(about === undefined ? "outcome" : { refusalOf: about.targetVoiceAgentId });
    },
  });

  /** "Publish now" — the existing publish of a draft (Versions' `publishDraftAgentVersion`), with its base answer. */
  const publish = useMutation({
    mutationFn: async (input: { readonly agentVersionId: string; readonly label: string; readonly liveVersionSeq: number; readonly acknowledgeBaseChange: boolean }) => {
      await gateway.publishDraftAgentVersion(voiceAgentId, input.agentVersionId, input.liveVersionSeq, input.acknowledgeBaseChange);
      return input;
    },
    onSuccess: async (input) => {
      setSavedLine(null);
      setEdited(null);
      setPublishOutcome({ kind: "published", label: input.label });
      setFocusTo("outcome");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await reread();
    },
    onError: async (error, input) => {
      const baseChanged = draftBaseChangedOf(error);
      setSavedLine(null);
      if (baseChanged !== null) {
        setPublishOutcome({ kind: "base_changed", label: input.label, message: apiErrorText(error, baseChanged.message), agentVersionId: input.agentVersionId });
      } else if (isStale(error) || (error instanceof ApiError && error.status === 409)) {
        setPublishOutcome({ kind: "refused", sentence: portalText("voiceAgentPass", "refusal.stale") });
      } else {
        setPublishOutcome({ kind: "refused", sentence: apiErrorText(error, portalText("operations", "nothing_was_changed_c60d5d2a")) });
      }
      setFocusTo("outcome");
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion });
      await reread();
    },
  });

  if (read.isError) {
    return (
      <>
        <h2 className="ch-section-title">{portalText("voiceAgentPass", "panel.title")}</h2>
        <p className="c-note c-note-critical" role="alert">
          {portalText("voiceAgentPass", "panel.read_failed")} {apiErrorText(read.error, portalText("operations", "reload_the_page_to_try_again_d6649089"))}
        </p>
      </>
    );
  }
  if (read.data === undefined) return null;
  const stored = read.data;
  if (!stored.offered) return null;

  const base = storedRowsOf(stored);
  const rows = edited ?? base;
  const changed = !sameRows(rows, base);
  const notAvailable = stored.availability === "not_available_yet";
  const sourceAudience = agentTemplateCode === null ? null : voiceAgentAudienceOf(agentTemplateCode);
  const full = rows.length >= VOICE_AGENT_PASS_TARGET_LIMIT;
  const listed = new Set(rows.map((one) => one.targetVoiceAgentId));
  const pickable = stored.candidate.filter((one) => !listed.has(one.voiceAgentId));
  const problemOf = (row: TargetRow): { readonly when: string | null; readonly note: string | null } => ({
    when: whenProblemOf(row.when),
    note: row.mode === "warm" ? noteProblemOf(row.note) : null,
  });
  const anyProblem = changed && rows.some((row) => {
    const problem = problemOf(row);
    return problem.when !== null || problem.note !== null;
  });
  const draft = stored.draft;
  // Review M1: the editor holds the published list the member sent, and that same draft still waits (a draft published
  // or replaced meanwhile ends the line).
  const equalsPublished =
    savedLine !== null && typeof savedLine === "object" && draft !== null && draft.agentVersionId === savedLine.agentVersionId ? savedLine : null;
  const savedText =
    equalsPublished !== null
      ? portalText("voiceAgentPass", "saved.equals_published", { label: equalsPublished.label })
      : savedLine === "draft"
        ? portalText("voiceAgentPass", "saved.draft")
        : savedLine === "unchanged"
          ? portalText("voiceAgentPass", "saved.unchanged")
          : savedLine === "stale"
            ? portalText("voiceAgentPass", "refusal.stale")
            : null;

  const edit = (next: readonly TargetRow[]): void => {
    setSavedLine(null);
    setRefusal(null);
    setPublishOutcome(null);
    setEdited(next);
  };
  const changeRow = (targetVoiceAgentId: string, next: Partial<TargetRow>): void => {
    edit(rows.map((one) => (one.targetVoiceAgentId === targetVoiceAgentId ? { ...one, ...next } : one)));
  };

  const heading = (
    <div className="vap-head">
      <h2 className="ch-section-title vap-title">{portalText("voiceAgentPass", "panel.title")}</h2>
      {notAvailable ? <Tag tone="neutral">{portalText("voiceAgentPass", "availability.tag")}</Tag> : null}
    </div>
  );

  if (stored.liveVersion === null) {
    return (
      <>
        {heading}
        <p className="ch-section-dek">{portalText("voiceAgentPass", "panel.dek")}</p>
        <div className="ch-card vap" data-voice-agent-pass="no-live-version">
          <p className="vap-note" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("voiceAgentPass", "refusal.not_ready")}</span>
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {heading}
      <p className="ch-section-dek">{portalText("voiceAgentPass", "panel.dek")}</p>

      <div className="ch-card vap" data-voice-agent-pass={notAvailable ? "not_available_yet" : "available"}>
        {notAvailable ? (
          <p className="vap-note" role="note">
            <Icon glyph={GLYPH.info} size="sm" />
            <span>{portalText("voiceAgentPass", "availability.note")}</span>
          </p>
        ) : null}

        {rows.length === 0 ? (
          <p className="ch-card-note vap-empty" ref={(element) => { listRef.current = element; }} tabIndex={-1}>
            {portalText("voiceAgentPass", "panel.empty")}
          </p>
        ) : (
          <ol className="vap-list" ref={(element) => { listRef.current = element; }} tabIndex={-1} aria-label={portalText("voiceAgentPass", "panel.title")}>
            {rows.map((row, index) => {
              const at = `${idBase}-${String(index)}`;
              const problem = changed ? problemOf(row) : { when: null, note: null };
              const stateText = targetStateTextOf(row.state);
              const warningText = row.warning.map((one) => warningTextOf(one, languageCode)).filter((one): one is string => one !== null);
              const rowRefusal = refusal !== null && refusal.targetVoiceAgentId === row.targetVoiceAgentId ? refusal.sentence : null;
              return (
                <li className="vap-row" key={row.targetVoiceAgentId} data-pass-target={row.targetVoiceAgentId}>
                  <div className="vap-row-head">
                    <div className="vap-row-name">
                      <span className="ch-card-label ch-card-label-flush">{portalText("voiceAgentPass", "field.target")}</span>
                      <span className="vap-row-title">
                        <span>{row.displayName}</span>
                        {row.audience === null ? null : <AudienceTag audience={row.audience} />}
                      </span>
                    </div>
                    {canEdit ? (
                      <Button
                        variant="ghost"
                        aria-label={portalText("voiceAgentPass", "action.remove_named", { name: row.displayName })}
                        onClick={() => {
                          edit(rows.filter((one) => one.targetVoiceAgentId !== row.targetVoiceAgentId));
                          setFocusTo("list");
                        }}
                      >
                        {portalText("voiceAgentPass", "action.remove")}
                      </Button>
                    ) : null}
                  </div>

                  {stateText === null && warningText.length === 0 ? null : (
                    <ul className="vap-row-notes">
                      {stateText === null ? null : (
                        <li className="vap-row-state" data-target-state={row.state}>
                          {stateText}
                        </li>
                      )}
                      {warningText.map((one) => (
                        <li className="vap-row-warning" key={one}>
                          <Icon glyph={GLYPH.warningCircle} size="sm" />
                          <span>{one}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <div className="field vap-field">
                    <label htmlFor={`${at}-when`}>{portalText("voiceAgentPass", "field.when")}</label>
                    <input
                      id={`${at}-when`}
                      className="input"
                      type="text"
                      autoComplete="off"
                      placeholder={portalText("voiceAgentPass", "field.when_placeholder")}
                      value={row.when}
                      disabled={!canEdit}
                      aria-invalid={problem.when === null ? undefined : true}
                      aria-describedby={problem.when === null ? undefined : `${at}-when-problem`}
                      onChange={(event) => {
                        changeRow(row.targetVoiceAgentId, { when: event.currentTarget.value });
                      }}
                    />
                    {problem.when === null ? null : (
                      <span className="vap-problem" id={`${at}-when-problem`}>
                        {problem.when}
                      </span>
                    )}
                  </div>

                  <fieldset className="vap-mode">
                    <legend className="ch-card-label">{portalText("voiceAgentPass", "field.mode")}</legend>
                    {(["warm", "cold"] as const).map((mode) => (
                      <label className="vap-choice" key={mode}>
                        <input
                          type="radio"
                          name={`${at}-mode`}
                          value={mode}
                          checked={row.mode === mode}
                          disabled={!canEdit}
                          onChange={() => {
                            changeRow(row.targetVoiceAgentId, { mode });
                          }}
                        />
                        <span>{portalText("voiceAgentPass", mode === "warm" ? "mode.warm" : "mode.cold")}</span>
                      </label>
                    ))}
                  </fieldset>

                  <div className="vap-back">
                    <SwitchControl
                      label={portalText("voiceAgentPass", "field.may_pass_back")}
                      checked={row.mayPassBack}
                      disabled={!canEdit}
                      onChange={(mayPassBack) => {
                        changeRow(row.targetVoiceAgentId, { mayPassBack });
                      }}
                    />
                    <span className="vap-help">{portalText("voiceAgentPass", "field.may_pass_back_help")}</span>
                  </div>

                  {row.mode === "warm" ? (
                    <div className="field vap-field">
                      <label htmlFor={`${at}-note`}>{portalText("voiceAgentPass", "field.note")}</label>
                      <input
                        id={`${at}-note`}
                        className="input"
                        type="text"
                        autoComplete="off"
                        placeholder={portalText("voiceAgentPass", "field.note_placeholder")}
                        value={row.note}
                        disabled={!canEdit}
                        aria-invalid={problem.note === null ? undefined : true}
                        aria-describedby={problem.note === null ? undefined : `${at}-note-problem`}
                        onChange={(event) => {
                          changeRow(row.targetVoiceAgentId, { note: event.currentTarget.value });
                        }}
                      />
                      {problem.note === null ? null : (
                        <span className="vap-problem" id={`${at}-note-problem`}>
                          {problem.note}
                        </span>
                      )}
                    </div>
                  ) : null}

                  {rowRefusal === null ? null : (
                    <p className="c-note c-note-critical vap-row-refusal" role="alert" id={`${idBase}-refusal-${row.targetVoiceAgentId}`} tabIndex={-1}>
                      {rowRefusal}
                    </p>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {canEdit ? (
          <div className="vap-add">
            <span title={full ? targetLimitText() : undefined}>
              <Button
                variant="secondary"
                disabled={full}
                onClick={() => {
                  setPicking(true);
                }}
              >
                <Icon glyph={GLYPH.plus} size="md" />
                <span>{portalText("voiceAgentPass", "panel.add")}</span>
              </Button>
            </span>
            {full ? <span className="vap-help">{targetLimitText()}</span> : null}
          </div>
        ) : null}

        {canEdit && (changed || savedLine !== null || (refusal !== null && refusal.targetVoiceAgentId === null) || draft !== null || publishOutcome !== null) ? (
          <div className="ch-card-actions vap-actions">
            {changed && equalsPublished === null ? (
              <Button
                variant="primary"
                disabled={anyProblem || save.isPending}
                onClick={() => {
                  setRefusal(null);
                  save.mutate({ rows, stored });
                }}
              >
                {save.isPending ? portalText("operations", "saving_56a2285c") : portalText("voiceAgentPass", "action.save")}
              </Button>
            ) : null}
            {changed && equalsPublished === null ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setEdited(null);
                  setRefusal(null);
                }}
              >
                {portalText("operations", "discard_36fff63c")}
              </Button>
            ) : null}
            {(!changed || equalsPublished !== null) && draft !== null ? (
              <Button
                variant="primary"
                disabled={publish.isPending}
                onClick={() => {
                  publish.mutate({ agentVersionId: draft.agentVersionId, label: draft.label, liveVersionSeq: stored.liveVersionSeq, acknowledgeBaseChange: false });
                }}
              >
                <Icon glyph={GLYPH.rocketLaunch} size="md" />
                <span>
                  {equalsPublished === null
                    ? portalText("voiceAgentPass", "action.publish")
                    : portalText("operations", "publish_value_05e1503f", { v0: draft.label })}
                </span>
              </Button>
            ) : null}
            {equalsPublished === null ? null : (
              <Button
                variant="secondary"
                onClick={() => {
                  onOpenAgentVersion(equalsPublished.agentVersionId);
                }}
              >
                {portalText("voiceAgentPass", "action.open_draft", { label: equalsPublished.label })}
              </Button>
            )}
            <span className="vap-outcome" ref={outcomeRef} tabIndex={-1}>
              {refusal === null || refusal.targetVoiceAgentId !== null ? null : (
                <span className="c-note c-note-critical" role="alert">
                  {refusal.sentence}
                </span>
              )}
              {savedText === null ? (
                !changed && draft !== null && publishOutcome === null ? (
                  <span className="ch-note vap-status">{portalText("voiceAgentPass", "saved.draft")}</span>
                ) : null
              ) : (
                <span className="ch-note vap-status" role="status">
                  {savedText}
                </span>
              )}
              {publishOutcome === null ? null : publishOutcome.kind === "published" ? (
                <span className="ch-note vap-status" role="status">
                  {portalText("operations", "value_is_live_91cf8d55", { v0: publishOutcome.label })}
                </span>
              ) : publishOutcome.kind === "base_changed" ? (
                <span className="c-note c-note-critical vap-base-changed" role="alert">
                  <span>
                    {publishOutcome.message} {portalText("operations", "nothing_was_changed_c60d5d2a")}
                  </span>
                  <Button
                    variant="ghost"
                    disabled={publish.isPending}
                    onClick={() => {
                      publish.mutate({
                        agentVersionId: publishOutcome.agentVersionId,
                        label: publishOutcome.label,
                        liveVersionSeq: stored.liveVersionSeq,
                        acknowledgeBaseChange: true,
                      });
                    }}
                  >
                    {portalText("operations", "publish_7db78801")}
                    {publishOutcome.label} {portalText("operations", "anyway_736b67b3")}
                  </Button>
                </span>
              ) : (
                <span className="c-note c-note-critical" role="alert">
                  {publishOutcome.sentence}
                </span>
              )}
            </span>
          </div>
        ) : null}

        <p className="ch-card-foot">{portalText("voiceAgentPass", "saved.applies")}</p>
      </div>

      {picking ? (
        <Dialog
          open
          title={portalText("voiceAgentPass", "panel.add")}
          onClose={() => {
            setPicking(false);
          }}
          actions={
            <Button
              variant="ghost"
              onClick={() => {
                setPicking(false);
              }}
            >
              {portalText("operations", "close_bbfa773e")}
            </Button>
          }
        >
          {pickable.length === 0 ? (
            <p className="vap-help">{portalText("voiceAgentPass", "picker.empty")}</p>
          ) : (
            <ul className="vap-picker">
              {pickable.map((candidate) => {
                const why = candidateRefusalTextOf(candidate, sourceAudience);
                const stateText = targetStateTextOf(candidate.state);
                return (
                  <li className="vap-candidate" key={candidate.voiceAgentId} data-pass-candidate={candidate.voiceAgentId} data-may-receive={candidate.mayReceive}>
                    <div className="vap-candidate-text">
                      <span className="vap-row-title">
                        <span>{candidate.displayName}</span>
                        <AudienceTag audience={candidate.audience} />
                      </span>
                      {why === null ? null : <span className="vap-help">{why}</span>}
                      {why === null && stateText !== null ? <span className="vap-help">{stateText}</span> : null}
                    </div>
                    <Button
                      variant="secondary"
                      disabled={why !== null}
                      onClick={() => {
                        setPicking(false);
                        edit([...rows, rowOfCandidate(candidate)]);
                      }}
                    >
                      {portalText("operations", "add_61cc55aa")}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </Dialog>
      ) : null}
    </>
  );
}
